import * as THREE from "three";
import { RING } from "./blobShadow";

/**
 * Where a boss's ring and plate go now that bosses are drawn ~2 cells tall (views/common.ts ARCH_SCALE, rares/bosses
 * review 2026-10-27). A boss's anchors height runs to the top of its set-pieces (the Warlord's banner, the Ironclad's
 * smokestacks, the Necromancer's staff fire), which at the new scale stand a cell or more above the body: the ring
 * sized by that height swallowed the road, and the badge plate floated far over the head. So:
 * - the rune ring hugs the footprint: the smaller of the height and the model's width/depth, × RING_PAD;
 * - the plate rides just above the head (the `head` part's own geometry: horns, crown and flames included).
 */
export const BOSS_SIZE = {
  /** The ring's diameter over the boss's drawn footprint. */
  ringPad: 1.1,
} as const;

/** Half extents of a model (anchors.json), world units at scale 1. */
export interface Footprint { readonly half_x?: number; readonly half_z?: number }

/** Rune ring width of a boss drawn at `scale` (never under RING.bossWidth). */
export function bossRingWidth(anchor: Footprint | undefined, height: number, scale: number): number {
  const across = anchor && anchor.half_x !== undefined && anchor.half_z !== undefined ? 2 * Math.max(anchor.half_x, anchor.half_z) : height;
  return Math.max(RING.bossWidth, Math.min(height, across) * scale * BOSS_SIZE.ringPad);
}

const _box = new THREE.Box3();
const _inv = new THREE.Matrix4();
const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();

/**
 * The top of `head` (its whole subtree) in `body`'s own space, unscaled: what a plate over the head clears. Null when
 * there is no head part or it has no geometry. Handles both a plain part hierarchy and a RigidRig (rigidRig.ts: the
 * parts merged into one skinned mesh per material whose bones are the part nodes - a head vertex is one bound to a node
 * of the head's subtree, placed by that node's world matrix).
 */
export function headTop(body: THREE.Object3D, head: THREE.Object3D | null): number | null {
  if (!head) return null;
  body.updateWorldMatrix(true, true);
  _box.makeEmpty();
  _inv.copy(body.matrixWorld).invert();
  const inHead = new Set<THREE.Object3D>();
  head.traverse((o) => { inHead.add(o); });
  head.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || (mesh as THREE.SkinnedMesh).isSkinnedMesh) return;
    const g = mesh.geometry;
    if (!g.boundingBox) g.computeBoundingBox();
    if (g.boundingBox) _box.union(g.boundingBox.clone().applyMatrix4(_m.multiplyMatrices(_inv, mesh.matrixWorld)));
  });
  body.traverse((o) => {
    const sk = o as THREE.SkinnedMesh;
    if (!sk.isSkinnedMesh) return;
    const bones = sk.skeleton.bones;
    const pick = bones.map((b) => inHead.has(b));
    if (!pick.some((x) => x)) return;
    const pos = sk.geometry.getAttribute("position"), idx = sk.geometry.getAttribute("skinIndex");
    if (!pos || !idx) return;
    for (let i = 0; i < pos.count; i++) {
      const b = idx.getX(i);
      if (!pick[b]) continue;
      _p.fromBufferAttribute(pos, i).applyMatrix4(bones[b].matrixWorld).applyMatrix4(_inv);
      _box.expandByPoint(_p);
    }
  });
  return _box.isEmpty() ? null : _box.max.y;
}
